import { useQuery } from "@tanstack/react-query";
import { useAmplifyAuth } from "../components/AmplifyAuthContext";
import { useFreshAuth } from "./useFreshAuth";

interface MFAStatus {
	isLoading: boolean;
	hasTOTP: boolean;
	hasSMS: boolean;
	preferredMFA: "SMS" | "TOTP" | "EMAIL" | "NONE" | null;
	error: string | null;
	needsFreshAuth: boolean;
	canSwitchToSMS: boolean;
}

export function useUserMFAStatus(): MFAStatus {
	const { fetchMFAPreference } = useAmplifyAuth();
	const freshAuth = useFreshAuth();

	const { data, isLoading, error } = useQuery({
		queryKey: ["userMFAStatus"],
		queryFn: async () => {
			const mfaPreference = await fetchMFAPreference();

			const hasTOTP = mfaPreference.enabled?.includes("TOTP") ?? false;
			const hasSMS = mfaPreference.enabled?.includes("SMS") ?? false;
			const preferredMFA =
				(mfaPreference.preferred as "SMS" | "TOTP" | "EMAIL") || "NONE";

			return {
				hasTOTP,
				hasSMS,
				preferredMFA,
				canSwitchToSMS: hasSMS,
			};
		},
		staleTime: 5 * 60 * 1000, // 5 minutes
		gcTime: 10 * 60 * 1000, // 10 minutes
		enabled: freshAuth.isFresh,
	});

	return {
		isLoading: freshAuth.isLoading || isLoading,
		hasTOTP: data?.hasTOTP ?? false,
		hasSMS: data?.hasSMS ?? false,
		preferredMFA: data?.preferredMFA ?? null,
		error:
			error instanceof Error ? error.message : error ? String(error) : null,
		needsFreshAuth: freshAuth.needsFreshAuth,
		canSwitchToSMS: data?.canSwitchToSMS ?? false,
	};
}
